import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

// styles
import './style.scss';

// layout components
import Preloader from 'components/Preloader';

// assets
import { LoadingOutlined } from '@ant-design/icons';

// components
import UserAvatar from 'components/UserAvatar';

// utils
import { getObjId } from 'utils/helpers/helper.general';

// hooks
import { useMounted } from 'utils/hooks';

const ChildPreview = ({
  view,
  loading,
  isActive,
  child,
  collapse,
  notifications,
  // actions
  onMount,
  onClick,
  // default props
  className,
  style,
}) => {
  const mainClassNames = classNames('child-preview', 'child', className);

  const location = useLocation();
  const mounted = useMounted();

  const activeChildRef = useRef(null);

  const [loadNotif, setLoadNotif] = useState(false);
  const [currentBadge, setCurrentBadge] = useState(false);

  // Added force reference for active item, when component is mounted
  useEffect(() => {
    if (!!isActive && !!onMount) {
      onMount(activeChildRef);
    }
  }, [isActive]);
  
  useEffect(() => {
    if (!!child) {
      mountNotification(child);
    }
  }, [notifications]);

  useEffect(() => {
    if (!!mounted && isActive) {
      mountNotification(child);
    }
  }, [isActive, location.pathname]);

  const mountNotification = (child) => {
    const result = notifications?.find(el => getObjId(el) == getObjId(child));
    setCurrentBadge(result?.amount);
    !!result?._id && setLoadNotif(false);
  }

  const onClickHandler = () => {
    !!onClick && !loading && onClick();
  }

  const renderName = (childData) => {
    let firstName = childData?.description?.firstname;
    if (childData?.description?.firstname?.includes(" ")) {
      const arr = childData?.description?.firstname?.split(" ");
      const [name, name2, ...rest] = arr;
      firstName = `${!!name ? name : ""}${!!name2 ? ` ${name2}`:""}`
    }
    return !!childData?.description && (
      <div className="child-preview__info">
        <span className="info-name">
          {firstName}{' '}
          {childData?.description?.lastname}
        </span>
      </div>
    );
  }

  return !!child && (
    <div
      className={classNames(
        mainClassNames,
        loading && 'loading',
        collapse && 'collapse',
        isActive && "active"
      )}
      onClick={onClickHandler}
      style={style}
      ref={isActive ? activeChildRef : null}
    >
      {loading && (
        <Preloader className="overlay-preloader" size="small" />
      )}
      {child?._id && view == 'full' && (
        <div className="child-preview-wrapper">
          {!!currentBadge && (<span className="badge">{currentBadge}</span>)}
          {!!loadNotif && (<LoadingOutlined className="badge-preloader" />)}
          <UserAvatar fontSize={22} width={54} height={54} user={child} />
          {renderName(child)}
        </div>
      )}
      {/* // compact view of student info (for header) */}
      {child?._id && view == 'small' && (
        <>
          <div className="child-preview-small-wrapper">
            {!!currentBadge && (<span className="badge">{currentBadge}</span>)}
            {!!loadNotif && (<LoadingOutlined className="badge-preloader" />)}
            <UserAvatar view='small' fontSize={16} width={54} height={54} user={child} />
          </div>
        </>
      )}
    </div>
  );

}

ChildPreview.defaultProps = {
  view: 'full',
  loading: false,
  collapse: false,
  isActive: false,
  notifications: []
};
ChildPreview.propTypes = {
  view: PropTypes.oneOf(['full', 'small']).isRequired,
  child: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.bool,
    PropTypes.string,
  ]),
  onClick: PropTypes.func,
};

export default ChildPreview;
